// @ts-strict-ignore
import { BasePasteOperationHelper } from '@project/components/label/helper/paste/base-paste-operation.helper';
import { CopiedArticleInformation, CopiedLabelStripArticleInformation } from '@project/components/article/model/copied-article-information';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Vector3 } from 'three';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ArticleRefreshData } from '@article/service/model/article-refresh-data';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CloneUtils } from '@shared/util/clone-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ReplaceLabelingArticleHelper } from '../../replace-labeling-helper/replace-labeling-article.helper';
import { LabelStrip, LabelStripTexture } from '../../replace-labeling-helper/replace-labeling-data';
import { MetaElementUsageIdentityVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ReplaceLabelingUtils } from '../../replace-labeling-helper/replace-labeling-utils';
import { ReplaceLabelingDataBuilder } from '../../replace-labeling-helper/replace-labeling-data-builder';
import { CopiedRangeOnLineArticleInformation } from '../../../article/model/copied-range-on-line-article-information';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ProjectModel } from '@project/model/project.model';
import { ProjectArticleService } from '@project/service/project-article.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

export class DynamicPasteOperationHelper extends BasePasteOperationHelper {
  private _rangeOnLineInfo: CopiedRangeOnLineArticleInformation;

  constructor(
    startPoint: Vector3,
    compositionContainer: CompositionContainer,
    rangeOnLineInfo: CopiedRangeOnLineArticleInformation,
    articleManager: ArticleManager,
    projectModel: ProjectModel,
    projectArticleService: ProjectArticleService,
    articleContainerLoader: ArticleContainerLoader,
    articleContainerFactory: ArticleContainerFactory,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private compositeArticleUtils: CompositeArticleUtils,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private connectionInfoService: ConnectionInfoService,
    detachArticleUsageService: DetachArticleUsageService
  ) {
    super(
      articleManager,
      projectModel,
      projectArticleService,
      articleContainerLoader,
      articleContainerFactory,
      detachArticleUsageService
    );
    this._startPoint = startPoint;
    this._compositionContainer = compositionContainer;
    this._rangeOnLineInfo = rangeOnLineInfo;
  }

  public async executePasteArticles(articleInfos: CopiedArticleInformation[]): Promise<string[]> {
    // paste label-strip articles
    const labelStripsInfos: CopiedLabelStripArticleInformation[] = this.filterLabelStripArticleInformation(articleInfos);
    await this.pasteLabelStrips(labelStripsInfos);
    // paste other dynamic articles (e.g. n-busbar, cover)
    return super.executePasteArticles(articleInfos.filter(ArrayUtils.notInArray, labelStripsInfos));
  }

  private filterLabelStripArticleInformation(articleInfos: CopiedArticleInformation[]): CopiedLabelStripArticleInformation[] {
    const labelStripsInfos: CopiedLabelStripArticleInformation[] = [];
    for (const info of articleInfos) {
      if (info instanceof CopiedLabelStripArticleInformation) {
        labelStripsInfos.push(info);
      }
    }
    return labelStripsInfos;
  }

  private async pasteLabelStrips(articleInfos: CopiedLabelStripArticleInformation[]): Promise<void> {
    if (ArrayUtils.isEmpty(articleInfos)) {
      return;
    }

    const labelStrips: LabelStrip[] = articleInfos.map(info => this.createLabelStripData(info));
    const createdContainers: ArticleContainer[] = await this.replaceLabelingHelper.createLabelingArticles(labelStrips, false);
    this.applyCompositeInformationToCreatedStrips(articleInfos, labelStrips, createdContainers);
    // add pasted label-strips to pending in order to show label-editor at the end of complete copy/paste operation
    this.replaceLabelingHelper.addTextCheckPendingContainers(createdContainers);
    ArrayUtils.mergeArrays(this._createdArticleGuids, ArticleContainerUtils.extractArticleGUIDs(createdContainers));
  }

  private applyCompositeInformationToCreatedStrips(
    articleInfos: CopiedLabelStripArticleInformation[],
    labelStrips: LabelStrip[],
    createdContainers: ArticleContainer[]
  ): void {
    for (const articleInfo of articleInfos) {
      const labelStrip: LabelStrip = labelStrips.find((strip) => strip.originalUsageId === articleInfo.articleUsageId);
      const articleContainers: ArticleContainer[] = createdContainers.filter(
        (container) => labelStrip.newUsageIds.indexOf(container.articleGUID) >= 0
      );
      for (const articleContainer of articleContainers) {
        CompositeArticleUtils.copyCompositeArticleInformationStackFromUsage(
          articleInfo.articleUsage,
          articleContainer.articleUsage,
          this.compositeArticleUtils.getCompositePartUsageIdFromUsage(articleInfo.articleUsage)
        );
      }
    }
  }

  private createLabelStripData(articleInfo: CopiedLabelStripArticleInformation): LabelStrip {
    const labelStrip: LabelStrip = new LabelStrip(this.connectionInfoService);
    labelStrip.originalUsageId = articleInfo.articleUsageId;
    labelStrip.articleId = articleInfo.articleUsage.articleId;
    labelStrip.articleVersion = articleInfo.articleUsage.articleVersion;

    const backwardAcceptingIds: MetaElementUsageIdentityVO[] = this.getBackwardAcceptingIds(articleInfo.articleUsage);
    labelStrip.onAdapter = this.replaceLabelingUtils.isStripOnAdapterByBackwardIds(backwardAcceptingIds);
    labelStrip.backwardAcceptingIds = backwardAcceptingIds;
    const textures = this.createLabelStripTextures(articleInfo);
    labelStrip.textures = textures;
    labelStrip.hasNotConnectedReferenceIds = this.replaceLabelingUtils.isNotConnectedReferenceIdExists(textures);
    return labelStrip;
  }

  private getBackwardAcceptingIds(articleUsage: ArticleUsageVO): MetaElementUsageIdentityVO[] {
    const backwardAcceptingIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(articleUsage);
    return backwardAcceptingIds.filter((id) => !!this._rangeOnLineInfo.getArticleInfoByArticleUsageId(id.articleUsageId));
  }

  private createLabelStripTextures(articleInfo: CopiedLabelStripArticleInformation): LabelStripTexture[] {
    const articleUsage: ArticleUsageVO = articleInfo.articleUsage;
    const labelStripTextures: LabelStripTexture[] = [];
    for (const copiedTextureInfo of articleInfo.textureConnectionInfos) {
      const textureUsage: TextureAreaUsageVO = ArticleUsageUtils.findTextureAreaUsageByName(articleUsage, copiedTextureInfo.metaElementName);
      const labelStripTexture: LabelStripTexture = ReplaceLabelingDataBuilder.buildLabelStripTextureByUsage(textureUsage, articleUsage, true);
      if (copiedTextureInfo.localRectangle) {
        labelStripTexture.localRectangle = copiedTextureInfo.localRectangle.clone();
      }
      labelStripTexture.distanceToPreviousConnection = copiedTextureInfo.distanceToPreviousConnection;

      labelStripTextures.push(labelStripTexture);
    }
    return labelStripTextures;
  }

  protected createArticleContainerByInfo(
    articleInfo: CopiedArticleInformation,
    refreshData: ArticleRefreshData,
    startPoint: Vector3,
    compositionId: string
  ): ArticleContainer {
    const createdContainer: ArticleContainer = super.createArticleContainerByInfo(articleInfo, refreshData, startPoint, compositionId);
    refreshData.addDynamicArticleInfo(CloneUtils.cloneArticleInfoVO(articleInfo.dynamicArticleInfo));
    refreshData.addDynamicArticleToMap(articleInfo.articleUsageId, this.getDynamicArticleVO(articleInfo));
    return createdContainer;
  }

  private getDynamicArticleVO(dynamicInfo: CopiedArticleInformation): ArticleVO {
    const dynamicUsage: ArticleUsageVO = dynamicInfo.articleUsage;
    let article: ArticleVO = this.articleManager.getCachedArticle(dynamicUsage.articleId, dynamicUsage.articleVersion);
    if (article) {
      article = CloneUtils.cloneArticleVO(article);
      article.metaElements = dynamicInfo.dynamicArticleMetaElements;
    }
    return article;
  }

  protected forceArticleRefreshOnAttach(): boolean {
    return false;
  }

  protected getMessageType(): string {
    return 'dynamicArticlesPasted';
  }

  public clear(): void {
    super.clear();
    this._rangeOnLineInfo = null;
  }
}
